"use client";

import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
  Button,
  MultiSelect,
  type OptionType,
} from "@oreme/ui";
import { useAtomValue, useAtom, useSetAtom } from "jotai";
import { ChevronRight, Binoculars } from "lucide-react";
import { useTranslations, useLocale } from "next-intl";
import React, { type ReactElement } from "react";
import { SingleValue } from "react-select";
import { toast } from "sonner";
import { useObservatory } from "@/hooks/sites";
import {
  filteredObservatoriesAtom,
  selectedObservatoryAtom,
  selectedSiteAtom,
  sidebarFilterObservatoryVisibilityAtom,
} from "@/store/sites";

function SitesSidebarFilterObservatory(): ReactElement {
  const t = useTranslations("sites.SidebarFilterObservatory");
  const locale = useLocale();
  const filteredObservatories = useAtomValue(filteredObservatoriesAtom);
  const [selectedObservatory, setSelectedObservatory] = useAtom(
    selectedObservatoryAtom,
  );
  const setSelectedSite = useSetAtom(selectedSiteAtom);
  const [isVisible, toggleVisibility] = useAtom(
    sidebarFilterObservatoryVisibilityAtom,
  );

  const { locate } = useObservatory();

  let options = filteredObservatories.map((observatory) => ({
    label: observatory.name[locale],
    value: observatory.id,
  }));
  options = options.sort((a, b) => a.label.localeCompare(b.label));

  const defaultValue = selectedObservatory
    ? { label: selectedObservatory.name[locale], value: selectedObservatory.id }
    : null;

  const handleChange = (newValue: SingleValue<OptionType>) => {
    const observatory = filteredObservatories.find(
      (observatory) => observatory.id === newValue!.value,
    );

    if (observatory && selectedObservatory !== observatory) {
      setSelectedObservatory(observatory);
      setSelectedSite(null);

      const location = locate(observatory);
      if (!location) {
        toast(t("error.title"), {
          description: t("error.description"),
        });
      }
    }
  };

  return (
    <Collapsible
      className="group/collapsible p-0 border rounded-lg"
      id="tour-4"
      open={isVisible}
      onOpenChange={toggleVisibility}
    >
      <div key="observatory">
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            className="flex flex-row gap-1 border-0"
            onClick={() => toggleVisibility()}
            asChild
          >
            <span className="flex flex-row gap-1">
              <Binoculars />
              <span>{t("observatory")}</span>
              <ChevronRight
                className={`ml-auto h-4 w-4 transition-transform ${isVisible ? "rotate-90" : ""}`}
              />
            </span>
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="flex flex-col gap-2 p-2">
          <MultiSelect
            unstyled={true}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            className="relative z-50 w-full"
            defaultValue={defaultValue}
            value={defaultValue}
            onChange={(newValue) => handleChange(newValue)}
            options={options}
            placeholder={t("searchPlaceholder")}
            noOptionsMessage={() => t("noOptionsMessage")}
            menuPlacement="auto"
            menuPortalTarget={
              globalThis.window === undefined ? null : document.body
            }
            isSearchable
          />
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

export { SitesSidebarFilterObservatory };
